import React from 'react';
import { Button, Grid } from '@material-ui/core';
import {
  EntityApiDefinitionCard,
  EntityConsumedApisCard,
  EntityConsumingComponentsCard,
  EntityHasApisCard,
  EntityProvidedApisCard,
  EntityProvidingComponentsCard,
} from '@backstage/plugin-api-docs';
import {
  EntityAboutCard,
  EntityDependsOnComponentsCard,
  EntityDependsOnResourcesCard,
  EntityHasComponentsCard,
  EntityHasResourcesCard,
  EntityHasSubcomponentsCard,
  EntityHasSystemsCard,
  EntityLayout,
  EntityLinksCard,
  EntitySwitch,
  EntityOrphanWarning,
  EntityProcessingErrorsPanel,
  isComponentType,
  isKind,
  hasCatalogProcessingErrors,
  isOrphan,
} from '@backstage/plugin-catalog';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  EntityUserProfileCard,
  EntityGroupProfileCard,
  EntityMembersListCard,
  EntityOwnershipCard,
} from '@backstage/plugin-org';
import { EntityTechdocsContent } from '@backstage/plugin-techdocs';
import { EmptyState, Progress } from '@backstage/core-components';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { PlatformOverviewCard } from '../home/PlatformOverviewCard';
import { PromotePrdCard } from './PromotePrdCard';
import { TracesContent } from './TracesContent';
import { LogsContent } from './LogsContent';
import { DbToolContent } from './DbToolContent';
import { FlywayContent } from './FlywayContent';
import { RumContent } from './RumContent';
import { EntityCostContent } from './EntityCostContent';
import { ServiceDetailCard } from './ServiceDetailCard';
import { GitlabCiCdContent } from './GitlabCiCdContent';
import { ArgoCdContent } from './ArgoCdContent';
import { GitlabOverviewCard } from './GitlabOverviewCard';
import { ServiceAlertThresholdCard } from './ServiceAlertThresholdCard';
import { InfrastructureStatusCard } from './InfrastructureStatusCard';
import { Link as RouterLink } from 'react-router-dom';
import {
  Direction,
  EntityCatalogGraphCard,
} from '@backstage/plugin-catalog-graph';
import {
  RELATION_API_CONSUMED_BY,
  RELATION_API_PROVIDED_BY,
  RELATION_CONSUMES_API,
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
  RELATION_HAS_PART,
  RELATION_PART_OF,
  RELATION_PROVIDES_API,
} from '@backstage/catalog-model';

import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';

import {
  EntityKubernetesContent,
  isKubernetesAvailable,
} from '@backstage/plugin-kubernetes';
import {
  isArgocdAvailable,
} from '@roadiehq/backstage-plugin-argo-cd';
import {
  isGitlabAvailable,
} from '@immobiliarelabs/backstage-plugin-gitlab';
import {
  ClusterContext,
  PodsTable,
  READY_COLUMNS,
  RESOURCE_COLUMNS,
  useKubernetesObjects,
  ErrorPanel,
} from '@backstage/plugin-kubernetes-react';
import { groupResponses } from '@backstage/plugin-kubernetes-common';

const GitlabCicdCard = () => {
  const { entity } = useEntity();
  const configApi = useApi(configApiRef);
  const annotations = entity?.metadata?.annotations ?? {};
  const slug = annotations['gitlab.com/project-slug'];

  const gitlabBaseUrl =
    configApi.getOptionalConfigArray('integrations.gitlab')?.[0]
      ?.getOptionalString('baseUrl') ?? '';

  if (!slug) {
    return (
      <EmptyState
        title="No CI/CD available for this entity"
        missing="info"
        description="`gitlab.com/project-slug` 주석이 필요합니다. catalog-info.yaml에 주석을 추가하고 다시 시도하세요."
        action={
          <Button
            variant="contained"
            color="primary"
            href="https://backstage.io/docs/features/software-catalog/well-known-annotations"
          >
            Read more
          </Button>
        }
      />
    );
  }

  const pipelinesUrl = `${gitlabBaseUrl}/${slug}/-/pipelines`;
  const jobsUrl = `${gitlabBaseUrl}/${slug}/-/jobs`;

  return (
    <EmptyState
      title="GitLab CI/CD"
      description="GitLab 파이프라인/잡 대시보드로 이동합니다."
      action={
        <>
          <Button
            component={RouterLink}
            to={{ pathname: pipelinesUrl }}
            target="_blank"
            variant="contained"
            color="primary"
            style={{ marginRight: 12 }}
          >
            Pipelines 열기
          </Button>
          <Button
            component={RouterLink}
            to={{ pathname: jobsUrl }}
            target="_blank"
            variant="outlined"
            color="primary"
          >
            Jobs 열기
          </Button>
        </>
      }
    />
  );
};

const ResourcePodsTable = () => {
  const { entity } = useEntity();
  const { kubernetesObjects, loading, error } = useKubernetesObjects(entity, 600000);

  if (loading) {
    return <Progress />;
  }

  if (error) {
    return (
      <ErrorPanel entityName={entity.metadata.name} errorMessage={error} />
    );
  }

  const items = kubernetesObjects?.items ?? [];
  if (items.length === 0) {
    return (
      <EmptyState
        title="No Kubernetes resources"
        missing="data"
        description={`No resources on any known clusters for ${entity.metadata.name}`}
      />
    );
  }

  return (
    <Grid container spacing={3} direction="column">
      {items.map((item, idx) => {
        const grouped = groupResponses(item.resources);
        const pods = grouped.pods ?? [];
        if (pods.length === 0) {
          return (
            <Grid item key={idx}>
              <EmptyState
                title="No Pods found"
                missing="data"
                description={`No pods with kubernetes-id for ${entity.metadata.name} in ${item.cluster.name}`}
              />
            </Grid>
          );
        }
        return (
          <Grid item key={idx}>
            <ClusterContext.Provider value={item.cluster}>
              <PodsTable
                pods={pods}
                columns={[READY_COLUMNS, RESOURCE_COLUMNS]}
              />
            </ClusterContext.Provider>
          </Grid>
        );
      })}
    </Grid>
  );
};

interface DashboardOption {
  id: string;
  label: string;
}

const SYSTEM_DASHBOARDS: DashboardOption[] = [
  { id: 'arcus-system-overview', label: 'System Overview' },
  { id: 'arcus-argocd-overview', label: 'ArgoCD' },
  { id: 'arcus-platform-main', label: 'Platform' },
  { id: 'arcus-dept-overview', label: 'Department' },
];

const SERVICE_DASHBOARDS: DashboardOption[] = [
  { id: 'arcus-service-overview', label: 'Service Overview' },
];

const LOGS_DASHBOARDS: DashboardOption[] = [
  { id: 'arcus-service-logs', label: 'Logs' },
];

const TRACES_DASHBOARDS: DashboardOption[] = [
  { id: 'arcus-service-traces', label: 'Traces' },
];

// Grafana iframe 폐기(성능) → 네이티브 컴포넌트 전용 렌더.
// dashboards/entityAware props는 호출부 호환을 위해 시그니처만 유지(미사용).
const GrafanaDashboardEmbed = ({
  nativeComponent,
}: {
  dashboards?: DashboardOption[];
  entityAware?: boolean;
  nativeComponent?: React.ReactNode;
}) => {
  return <>{nativeComponent ?? null}</>;
};

const techdocsContent = (
  <EntityTechdocsContent>
    <TechDocsAddons>
      <ReportIssue />
    </TechDocsAddons>
  </EntityTechdocsContent>
);

const cicdContent = (
  <EntitySwitch>
    <EntitySwitch.Case if={isGitlabAvailable}>
      <Grid container spacing={2} direction="column">
        <Grid item>
          <PromotePrdCard />
        </Grid>
        <Grid item>
          <GitlabCiCdContent />
        </Grid>
      </Grid>
    </EntitySwitch.Case>
    <EntitySwitch.Case>
      <GitlabCicdCard />
    </EntitySwitch.Case>
  </EntitySwitch>
);

const entityWarningContent = (
  <>
    <EntitySwitch>
      <EntitySwitch.Case if={isOrphan}>
        <Grid item xs={12}>
          <EntityOrphanWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    {/* Relation Warning — API missing은 무시 (openapi.yaml 미등록 서비스 허용) */}

    <EntitySwitch>
      <EntitySwitch.Case if={hasCatalogProcessingErrors}>
        <Grid item xs={12}>
          <EntityProcessingErrorsPanel />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
  </>
);

const overviewContent = (
  <Grid container spacing={3} alignItems="stretch">
    {entityWarningContent}
    <Grid item md={6}>
      <EntityAboutCard variant="gridItem" />
    </Grid>
    <Grid item md={6} xs={12}>
      <EntityCatalogGraphCard variant="gridItem" height={400} />
    </Grid>

    <Grid item md={6} xs={12}>
      <GitlabOverviewCard />
    </Grid>
    <Grid item md={6} xs={12}>
      <ServiceAlertThresholdCard />
    </Grid>
    <Grid item md={6} xs={12}>
      <InfrastructureStatusCard />
    </Grid>
    <Grid item md={6} xs={12}>
      <EntityLinksCard />
    </Grid>
    <Grid item md={8} xs={12}>
      <EntityHasSubcomponentsCard variant="gridItem" />
    </Grid>
  </Grid>
);

const serviceEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/ci-cd" title="CI/CD">
      {cicdContent}
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/argocd"
      title="Argo CD"
      if={isArgocdAvailable}
    >
      <ArgoCdContent />
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/kubernetes"
      title="Kubernetes"
      if={isKubernetesAvailable}
    >
      <EntityKubernetesContent />
    </EntityLayout.Route>

    <EntityLayout.Route path="/monitoring" title="Monitoring">
      <GrafanaDashboardEmbed
        dashboards={SERVICE_DASHBOARDS}
        entityAware
        nativeComponent={<ServiceDetailCard />}
      />
    </EntityLayout.Route>

    <EntityLayout.Route path="/logs" title="Logs">
      <GrafanaDashboardEmbed dashboards={LOGS_DASHBOARDS} entityAware nativeComponent={<LogsContent />} />
    </EntityLayout.Route>

    <EntityLayout.Route path="/traces" title="Traces">
      <GrafanaDashboardEmbed
        dashboards={TRACES_DASHBOARDS}
        entityAware
        nativeComponent={<TracesContent />}
      />
    </EntityLayout.Route>

    {/* FE/BE 통합형 RUM: annotation arcus.dev/has-rum=true 인 서비스만 RUM 탭 노출
        (Thymeleaf SSR + static/ FE 번들 패턴, 예: workday-be-main/wdc/sys, 세션79 도입, 세션87 annotation 기반 복구) */}
    <EntityLayout.Route
      path="/rum"
      title="RUM"
      if={(entity: any) => entity?.metadata?.annotations?.['arcus.dev/has-rum'] === 'true'}
    >
      <RumContent />
    </EntityLayout.Route>

    <EntityLayout.Route path="/api" title="API">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityProvidedApisCard />
        </Grid>
        <Grid item md={6}>
          <EntityConsumedApisCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/dependencies" title="Dependencies">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityDependsOnComponentsCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          <EntityDependsOnResourcesCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/cost" title="Cost">
      <EntityCostContent />
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>
  </EntityLayout>
);

const websiteEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/ci-cd" title="CI/CD">
      {cicdContent}
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/argocd"
      title="Argo CD"
      if={isArgocdAvailable}
    >
      <ArgoCdContent />
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/kubernetes"
      title="Kubernetes"
      if={isKubernetesAvailable}
    >
      <EntityKubernetesContent />
    </EntityLayout.Route>

    <EntityLayout.Route path="/monitoring" title="Monitoring">
      <GrafanaDashboardEmbed
        dashboards={SERVICE_DASHBOARDS}
        entityAware
        nativeComponent={<ServiceDetailCard />}
      />
    </EntityLayout.Route>

    <EntityLayout.Route path="/logs" title="Logs">
      <GrafanaDashboardEmbed dashboards={LOGS_DASHBOARDS} entityAware nativeComponent={<LogsContent />} />
    </EntityLayout.Route>

    <EntityLayout.Route path="/traces" title="Traces">
      <GrafanaDashboardEmbed
        dashboards={TRACES_DASHBOARDS}
        entityAware
        nativeComponent={<TracesContent />}
      />
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/rum"
      title="RUM"
      if={(entity: any) => entity?.metadata?.annotations?.['arcus.dev/has-rum'] === 'true'}
    >
      <RumContent />
    </EntityLayout.Route>

    <EntityLayout.Route path="/dependencies" title="Dependencies">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityDependsOnComponentsCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          <EntityDependsOnResourcesCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/cost" title="Cost">
      <EntityCostContent />
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>
  </EntityLayout>
);

/**
 * NOTE: This page is designed to work on small screens such as mobile devices.
 * This is based on Material UI Grid. If breakpoints are used, each grid item must set the `xs` prop to a column size or to `true`,
 * since this does not default. If no breakpoints are used, the items will equitably share the available space.
 * https://material-ui.com/components/grid/#basic-grid.
 */

const defaultEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>
  </EntityLayout>
);

const resourceEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/ci-cd" title="CI/CD">
      {cicdContent}
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/argocd"
      title="Argo CD"
      if={isArgocdAvailable}
    >
      <ArgoCdContent />
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/kubernetes"
      title="Kubernetes"
      if={isKubernetesAvailable}
    >
      <EntityKubernetesContent />
      <ResourcePodsTable />
    </EntityLayout.Route>

    <EntityLayout.Route path="/monitoring" title="Monitoring">
      <GrafanaDashboardEmbed
        dashboards={SERVICE_DASHBOARDS}
        entityAware
        nativeComponent={<ServiceDetailCard />}
      />
    </EntityLayout.Route>

    <EntityLayout.Route path="/logs" title="Logs">
      <GrafanaDashboardEmbed dashboards={LOGS_DASHBOARDS} entityAware nativeComponent={<LogsContent />} />
    </EntityLayout.Route>

    <EntityLayout.Route path="/traces" title="Traces">
      <GrafanaDashboardEmbed
        dashboards={TRACES_DASHBOARDS}
        entityAware
        nativeComponent={<TracesContent />}
      />
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/db-tool"
      title="DB Tool"
      if={(entity: any) => entity?.spec?.type === 'database'}
    >
      <DbToolContent />
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/flyway"
      title="Flyway"
      if={(entity: any) => entity?.spec?.type === 'database'}
    >
      <FlywayContent />
    </EntityLayout.Route>

    <EntityLayout.Route path="/cost" title="Cost">
      <EntityCostContent />
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>
  </EntityLayout>
);

const componentPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isComponentType('service')}>
      {serviceEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case if={isComponentType('website')}>
      {websiteEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);

const apiPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard variant="gridItem" height={400} />
        </Grid>
        <Grid item md={4} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid container item md={12}>
          <Grid item md={6}>
            <EntityProvidingComponentsCard />
          </Grid>
          <Grid item md={6}>
            <EntityConsumingComponentsCard />
          </Grid>
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/definition" title="Definition">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <EntityApiDefinitionCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const userPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityUserProfileCard variant="gridItem" />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const groupPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityGroupProfileCard variant="gridItem" />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard variant="gridItem" />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityMembersListCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityLinksCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const systemPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard variant="gridItem" />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard variant="gridItem" height={400} />
        </Grid>
        <Grid item md={4} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid item md={8}>
          <EntityHasComponentsCard variant="gridItem" />
        </Grid>
        <EntitySwitch>
          <EntitySwitch.Case if={e => Boolean((e.spec as any)?.providesApis?.length)}>
            <Grid item md={6}>
              <EntityHasApisCard variant="gridItem" />
            </Grid>
          </EntitySwitch.Case>
        </EntitySwitch>
        <Grid item md={6}>
          <EntityHasResourcesCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    <EntityLayout.Route path="/diagram" title="Diagram">
      <EntityCatalogGraphCard
        variant="gridItem"
        direction={Direction.TOP_BOTTOM}
        title="System Diagram"
        height={700}
        relations={[
          RELATION_PART_OF,
          RELATION_HAS_PART,
          RELATION_API_CONSUMED_BY,
          RELATION_API_PROVIDED_BY,
          RELATION_CONSUMES_API,
          RELATION_PROVIDES_API,
          RELATION_DEPENDENCY_OF,
          RELATION_DEPENDS_ON,
        ]}
        unidirectional={false}
      />
    </EntityLayout.Route>
    <EntityLayout.Route path="/monitoring" title="Monitoring">
      <GrafanaDashboardEmbed
        dashboards={SYSTEM_DASHBOARDS}
        entityAware
        nativeComponent={<PlatformOverviewCard />}
      />
    </EntityLayout.Route>

    <EntityLayout.Route path="/logs" title="Logs">
      <GrafanaDashboardEmbed dashboards={LOGS_DASHBOARDS} entityAware nativeComponent={<LogsContent />} />
    </EntityLayout.Route>

    <EntityLayout.Route path="/traces" title="Traces">
      <GrafanaDashboardEmbed
        dashboards={TRACES_DASHBOARDS}
        entityAware
        nativeComponent={<TracesContent />}
      />
    </EntityLayout.Route>

  </EntityLayout>
);

const domainPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard variant="gridItem" />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard variant="gridItem" height={400} />
        </Grid>
        <Grid item md={6}>
          <EntityHasSystemsCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

export const entityPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isKind('component')} children={componentPage} />
    <EntitySwitch.Case if={isKind('api')} children={apiPage} />
    <EntitySwitch.Case if={isKind('group')} children={groupPage} />
    <EntitySwitch.Case if={isKind('user')} children={userPage} />
    <EntitySwitch.Case if={isKind('system')} children={systemPage} />
    <EntitySwitch.Case if={isKind('domain')} children={domainPage} />
    <EntitySwitch.Case if={isKind('resource')} children={resourceEntityPage} />

    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);
